import {
  AdvancedMarker,
  APILoadingStatus,
  APIProvider,
  Map,
  useApiLoadingStatus,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import type { ComponentProps, ReactNode } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

declare global {
  interface Window {
    /** Google Maps JS API hook — invoked by the loader when the API key is rejected. */
    gm_authFailure?: () => void;
  }
}

type LatLng = { lat: number; lng: number };

/**
 * One driver marker on the map. `position` is `null` while the driver has no fix yet (e.g. just came online,
 * GPS not received). Consumers can choose to render a "stale last-known" pin or skip entirely — for the
 * default marker, null positions are skipped.
 */
export type DriverPin = {
  driverId: string;
  position: LatLng | null;
};

type Props = {
  apiKey: string;
  /** Pickup point — green leaf pin. Hidden when null. Useful for single-order views (the dispensary). */
  dispensary?: (LatLng & { label: string }) | null;
  /** Destination — blue house pin. Hidden when null. Useful for single-order views (the customer). */
  delivery?: (LatLng & { label: string }) | null;
  /**
   * Driver markers. One entry for single-driver views (customer); N for fleet views (dispensary, transporter).
   * Each pin renders the default car icon unless `renderDriverMarker` is provided.
   */
  drivers: DriverPin[];
  /** Fallback center used only when no marker has coordinates. */
  fallbackCenter: LatLng;
  /**
   * Optional Google Maps JS libraries to load via the underlying `APIProvider`.
   *
   * Exposed because the Maps JS SDK loads exactly once per browser session: whichever `APIProvider`
   * mounts first wins the library config, and subsequent mounts with different `libraries` get a noisy
   * "API has already been loaded with different parameters" warning + their config silently dropped.
   *
   * Consumers that also use a Maps library elsewhere in the app (e.g. Place Autocomplete on a checkout
   * screen) must pass it here so the global SDK config is consistent regardless of mount order. The
   * consumer owns the "why we need this library" knowledge, not the shared package.
   */
  libraries?: ComponentProps<typeof APIProvider>["libraries"];
  /**
   * Google Maps `mapId` — controls Cloud-Console-defined styling and unlocks Advanced Markers.
   * Each consumer declares its own ID so a customer-styled tile theme isn't accidentally inherited by
   * a dispensary or transporter dashboard. Defaults to the neutral `"live-driver-map"`.
   */
  mapId?: string;
  /**
   * Optional per-driver marker renderer. Receives a `DriverPin` with `position != null` (pins without a fix
   * are skipped before this is called). Use to add selection chrome, custom titles, click handlers, etc.
   * Default behavior renders an `AdvancedMarker` with the green car icon at `pin.position`.
   */
  renderDriverMarker?: (pin: DriverPin & { position: LatLng }) => ReactNode;
  /**
   * Optional extra map children. Rendered as direct children of the underlying `<Map>` after the default
   * markers — useful for `<Polyline>` overlays (e.g. transporter's selected-driver next-stop route).
   */
  children?: ReactNode;
};

function MapsAuthHelp() {
  return (
    <p className="rounded-md border border-amber-light bg-amber-950/40 px-3 py-2 text-xs text-ink/90">
      Google Maps rejected this API key or could not load. In{" "}
      <a
        href="https://console.cloud.google.com/google/maps-apis/overview"
        className="text-green underline hover:text-green"
        target="_blank"
        rel="noreferrer"
      >
        Google Cloud Console
      </a>
      : enable <span className="font-mono">Maps JavaScript API</span>, ensure billing is active, and under key
      restrictions add HTTP referrers for this origin (e.g.{" "}
      <span className="font-mono text-amber">http://localhost:5173/*</span> for Vite).
    </p>
  );
}

/**
 * Inline SVG used as the driver marker glyph. Rendered inside an
 * `AdvancedMarker` so it reads as visually distinct from the static
 * dispensary pin. Sized to match a Pin's footprint so all three
 * markers feel proportional. Filled with the brand deep-green by
 * default; consumers (e.g. transporter dashboard) pass `color` to
 * recolor for selection chrome.
 *
 * Exported so fleet-view consumers that need selection/dimming chrome
 * can wrap this icon instead of reimplementing the silhouette.
 */
export function DriverIcon({ color = "#2f5d50" }: { color?: string } = {}) {
  return (
    <svg
      width={36}
      height={36}
      viewBox="0 0 36 36"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
      aria-hidden
    >
      <circle cx={18} cy={18} r={16} fill="#ffffff" stroke={color} strokeWidth={2.5} />
      {/* Side-view car silhouette — hood + cabin trapezoid sitting on
       *  two wheels. Reads as a passenger car (not a truck), which
       *  matches how drivers' personal vehicles show up in the fleet. */}
      <path
        d="M7 22 L7 19 L11 14 L25 14 L29 19 L29 22 Z"
        fill={color}
      />
      <circle cx={12} cy={23} r={2.5} fill="#1a1a1a" />
      <circle cx={24} cy={23} r={2.5} fill="#1a1a1a" />
    </svg>
  );
}

/**
 * Inline SVG house silhouette used as the customer delivery marker.
 * Mirrors the `DriverIcon` visual treatment (white circle + brand
 * outline + filled glyph) so the three markers — pickup, delivery,
 * driver — read as a coherent set rather than a mix of stock pins and
 * custom icons. Blue keeps the "delivery destination" colour cue from
 * the prior `<Pin background="#2563eb">` so returning users still
 * recognise it.
 */
function DeliveryIcon() {
  return (
    <svg
      width={36}
      height={36}
      viewBox="0 0 36 36"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
      aria-hidden
    >
      <circle cx={18} cy={18} r={16} fill="#ffffff" stroke="#2563eb" strokeWidth={2.5} />
      {/* House: pitched roof peaking at (18,9), body from y=17 to y=25,
       *  door cutout (6 wide × 4 tall) centred on the bottom edge. */}
      <path
        d="M18 9 L27 17 L27 25 L21 25 L21 21 L15 21 L15 25 L9 25 L9 17 Z"
        fill="#2563eb"
      />
    </svg>
  );
}

/**
 * Inline SVG cannabis-leaf silhouette used as the dispensary (pickup)
 * marker. Mirrors the `DriverIcon` / `DeliveryIcon` visual treatment
 * (white circle + brand outline + filled glyph) so all three markers
 * read as a coherent custom-icon set. The leaf is a stylized
 * 7-leaflet design — outline traced clockwise from the top center
 * leaflet, alternating outer tip → inner notch → next outer tip.
 * Symmetric across `x = 18`. The 7-leaflet shape is the iconic
 * cannabis silhouette; reads instantly even at 36×36.
 *
 * The deep-green stroke + fill tracks the brand colour and keeps the
 * "pickup" colour cue from the prior `<Pin background="#2f5d50">` so
 * returning users still recognise it.
 */
function DispensaryIcon() {
  return (
    <svg
      width={36}
      height={36}
      viewBox="0 0 36 36"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
      aria-hidden
    >
      <circle cx={18} cy={18} r={16} fill="#ffffff" stroke="#2f5d50" strokeWidth={2.5} />
      {/* 7-leaflet leaf — tips at the cardinal-ish points around the
       *  centre at (18,18); inner notches between each pair of tips
       *  give the classic serrated silhouette. Coordinate map:
       *    top centre tip            (18, 6)
       *    upper-right tip           (25, 11)
       *    mid-right tip             (28, 18)
       *    lower-right tip           (24, 24)
       *    bottom (stem base)        (18, 28)
       *    lower-left tip            (12, 24)
       *    mid-left tip              (8, 18)
       *    upper-left tip            (11, 11)
       *  Stem: short vertical line from (18, 28) to (18, 30). */}
      <path
        d="M18 6 L19.5 13 L25 11 L21.5 16 L28 18 L21.5 19 L24 24 L19.5 22 L18 28 L16.5 22 L12 24 L14.5 19 L8 18 L14.5 16 L11 11 L16.5 13 Z"
        fill="#2f5d50"
      />
      <path d="M18 26 L18 30" stroke="#2f5d50" strokeWidth={1.5} strokeLinecap="round" />
    </svg>
  );
}

/**
 * Auto-fit the map to whatever markers exist on **first mount only**.
 * Per the locked decision: orient the user once when the tracking page
 * opens, then stay fixed even as the driver marker moves (continuous
 * recentering is jumpy and disorients the user).
 *
 * Re-fits if the marker set materially changes — e.g., the dispensary
 * coords arrive after a delayed backfill, or the driver appears for
 * the first time. We track an `initialFitDone` ref to scope this to
 * the first non-empty render: subsequent driver-only updates are
 * ignored.
 */
function AutoFitOnMount({
  dispensary,
  delivery,
  driverPositions,
}: {
  dispensary: LatLng | null;
  delivery: LatLng | null;
  driverPositions: LatLng[];
}) {
  const map = useMap();
  // `useMapsLibrary` lets us reach typed Maps SDK constructors
  // (`LatLngBounds`) without relying on a global `google` namespace —
  // this project has no `@types/google.maps` installed.
  const coreLib = useMapsLibrary("core");
  const initialFitDone = useRef(false);
  // Read inside the effect via ref so position deltas don't refit the viewport. Semantic: fit once
  // the moment we first have any marker, then leave the viewport alone — continuous recentering
  // would be jumpy as the driver moves.
  const driverPositionsRef = useRef(driverPositions);
  driverPositionsRef.current = driverPositions;
  // Boolean flips false → true on the first driver arrival; only this transition triggers re-evaluation
  // (combined with the `initialFitDone` guard, that's "fit once on first non-empty mount").
  const hasAnyDriverPosition = driverPositions.length > 0;

  useEffect(() => {
    if (!map || !coreLib) return;
    if (initialFitDone.current) return;

    const points: LatLng[] = [];
    if (dispensary) points.push(dispensary);
    if (delivery) points.push(delivery);
    for (const p of driverPositionsRef.current) points.push(p);

    if (points.length === 0) return;

    if (points.length === 1) {
      // Single marker — center on it; pick a reasonable city-block zoom.
      map.setCenter(points[0]!);
      map.setZoom(15);
    } else {
      const bounds = new coreLib.LatLngBounds();
      for (const p of points) bounds.extend(p);
      map.fitBounds(bounds, /* padding (px) */ 64);
    }

    initialFitDone.current = true;
  }, [map, coreLib, dispensary, delivery, hasAnyDriverPosition]);

  return null;
}

function MapBody({
  dispensary,
  delivery,
  drivers,
  fallbackCenter,
  mapId,
  renderDriverMarker,
  children,
}: Omit<Props, "apiKey" | "libraries">) {
  const status = useApiLoadingStatus();

  if (status === APILoadingStatus.FAILED) {
    return <MapsAuthHelp />;
  }

  const driverPositions = drivers
    .map((d) => d.position)
    .filter((p): p is LatLng => p != null);
  const firstDriver = driverPositions[0] ?? null;

  // Initial center: prefer dispensary > delivery > first driver > fallback. The
  // `AutoFitOnMount` helper below re-frames as soon as the API loads, so
  // this is just the pre-fit default to avoid a Denver-flash for orders
  // with all three coords already present.
  const initial = dispensary ?? delivery ?? firstDriver ?? fallbackCenter;
  const anyMarker = dispensary != null || delivery != null || firstDriver != null;

  return (
    <Map
      mapId={mapId ?? "live-driver-map"}
      defaultCenter={initial}
      defaultZoom={anyMarker ? 14 : 11}
      gestureHandling="greedy"
      style={{ width: "100%", height: 220, borderRadius: 8 }}
      disableDefaultUI={false}
    >
      <AutoFitOnMount
        dispensary={dispensary ? { lat: dispensary.lat, lng: dispensary.lng } : null}
        delivery={delivery ? { lat: delivery.lat, lng: delivery.lng } : null}
        driverPositions={driverPositions}
      />
      {dispensary ? (
        <AdvancedMarker position={dispensary} title={dispensary.label}>
          {/* Cannabis-leaf icon = pickup (dispensary). Visual weight
           *  matches the other two custom markers; brand deep-green
           *  preserves the colour cue from the prior stock pin. */}
          <DispensaryIcon />
        </AdvancedMarker>
      ) : null}
      {delivery ? (
        <AdvancedMarker position={delivery} title={delivery.label}>
          {/* House icon = delivery destination. Blue stays from the
           *  earlier stock-pin treatment so the colour mapping
           *  (green=pickup, blue=delivery, green-car=driver) holds. */}
          <DeliveryIcon />
        </AdvancedMarker>
      ) : null}
      {drivers.map((pin) => {
        if (pin.position == null) return null;
        const pinWithPos = { ...pin, position: pin.position };
        if (renderDriverMarker) {
          return (
            <DriverMarkerSlot key={pin.driverId} pin={pinWithPos} render={renderDriverMarker} />
          );
        }
        return (
          <AdvancedMarker key={pin.driverId} position={pin.position} title="Driver">
            <DriverIcon />
          </AdvancedMarker>
        );
      })}
      {children}
    </Map>
  );
}

/** Lets consumers return any node tree for a driver marker; we just give it the resolved pin. */
function DriverMarkerSlot({
  pin,
  render,
}: {
  pin: DriverPin & { position: LatLng };
  render: (pin: DriverPin & { position: LatLng }) => ReactNode;
}) {
  return <>{render(pin)}</>;
}

/**
 * Google Map for live driver tracking. Renders up to three markers —
 * pickup (dispensary), delivery (customer), driver (live GPS) — and
 * auto-fits the viewport to whichever subset is present on first
 * mount. The driver marker uses a custom SVG to stand apart from the
 * static dispensary/delivery pins.
 *
 * Requires a valid Google Maps JavaScript API key. The `mapId` enables
 * **Advanced Markers** which the driver icon depends on — see Google
 * Maps Platform "Map IDs" in Cloud Console; a vector-rendered map
 * style with no extra customisation works fine.
 */
export function LiveDriverMap(props: Props) {
  const [authRejected, setAuthRejected] = useState(false);

  useLayoutEffect(() => {
    const prev = window.gm_authFailure;
    window.gm_authFailure = () => {
      setAuthRejected(true);
      prev?.();
    };
    return () => {
      window.gm_authFailure = prev;
    };
  }, []);

  if (authRejected) {
    return <MapsAuthHelp />;
  }

  return (
    <APIProvider apiKey={props.apiKey} libraries={props.libraries}>
      <MapBody
        dispensary={props.dispensary}
        delivery={props.delivery}
        drivers={props.drivers}
        fallbackCenter={props.fallbackCenter}
        mapId={props.mapId}
        renderDriverMarker={props.renderDriverMarker}
      >
        {props.children}
      </MapBody>
    </APIProvider>
  );
}
